import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Upload, message, Avatar, Space, Switch, Divider } from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import api, { imageBaseUrl } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const SystemSettings = () => {
  const [form] = Form.useForm();
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/settings');
        const settings = response.data;
        if (settings) {
          form.setFieldsValue({
            schoolName: settings.school_name,
            address: settings.address,
            email: settings.email,
            phone: settings.phone,
            allow_teacher_feedback_entry: settings.allow_teacher_feedback_entry !== false,
            allow_teacher_attendance_entry: settings.allow_teacher_attendance_entry !== false,
            allow_teacher_mark_entry: settings.allow_teacher_mark_entry !== false,
            allow_teacher_mark_view: settings.allow_teacher_mark_view !== false,
            allow_teacher_student_view: settings.allow_teacher_student_view !== false,
            allow_teacher_student_edit: settings.allow_teacher_student_edit !== false,
          });
          if (settings.logo_url) {
            setLogoUrl(`${imageBaseUrl}${settings.logo_url}`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        message.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [form]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const payload = {
        school_name: values.schoolName,
        address: values.address,
        email: values.email,
        phone: values.phone,
        allow_teacher_feedback_entry: !!values.allow_teacher_feedback_entry,
        allow_teacher_attendance_entry: !!values.allow_teacher_attendance_entry,
        allow_teacher_mark_entry: !!values.allow_teacher_mark_entry,
        allow_teacher_mark_view: !!values.allow_teacher_mark_view,
        allow_teacher_student_view: !!values.allow_teacher_student_view,
        allow_teacher_student_edit: !!values.allow_teacher_student_edit,
      };

      await api.put('/settings', payload);
      message.success('System settings updated successfully');
      // Optionally trigger a global refresh if we had a context for settings
      window.location.reload(); // Simple way to refresh layout for now
    } catch (error) {
      console.error('Failed to update settings:', error);
      message.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await api.post('/settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newLogoUrl = `${imageBaseUrl}${response.data.logo_url}`;
      setLogoUrl(newLogoUrl);
      onSuccess(response.data);
      message.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
      message.error(`${file.name} upload failed.`);
    }
  };

  return (
    <Card title="System Configuration" bordered={false} loading={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item label="School Logo">
          <Space direction="vertical" align="center" style={{ width: '100%', marginBottom: 16 }}>
            <Avatar
              size={100}
              src={logoUrl}
              icon={!logoUrl && <UserOutlined />}
              shape="square"
            />
            {isSuperAdmin && (
              <Upload
                customRequest={handleUpload}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>Change Logo</Button>
              </Upload>
            )}
          </Space>
        </Form.Item>

        <Form.Item
          name="schoolName"
          label="School Name"
          rules={[{ required: true, message: 'Please enter school name' }]}
        >
          <Input size="large" disabled={!isSuperAdmin} />
        </Form.Item>

        <Form.Item
          name="address"
          label="Address"
        >
          <Input.TextArea rows={3} disabled={!isSuperAdmin} />
        </Form.Item>

        <Form.Item
          name="email"
          label="Contact Email"
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input disabled={!isSuperAdmin} />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Contact Phone"
        >
          <Input disabled={!isSuperAdmin} />
        </Form.Item>

        <Divider orientation="left">Teacher Feature Permissions (Superadmin Controls)</Divider>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <Form.Item name="allow_teacher_feedback_entry" label="Tutor Feedback Entry" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
          <Form.Item name="allow_teacher_attendance_entry" label="Attendance Entry" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
          <Form.Item name="allow_teacher_mark_entry" label="Mark Entry" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
          <Form.Item name="allow_teacher_mark_view" label="Mark View" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
          <Form.Item name="allow_teacher_student_view" label="Student Details View" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
          <Form.Item name="allow_teacher_student_edit" label="Student Details Edit" valuePropName="checked">
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" disabled={!isSuperAdmin} />
          </Form.Item>
        </div>

        {isSuperAdmin && (
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </Form.Item>
        )}
      </Form>
    </Card>
  );
};

export default SystemSettings;
